import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE, fadeUp } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

const Card = ({ card, index, active, onActivate, onClick }) => {
    return (
        <motion.div
            onMouseEnter={onActivate}
            onClick={onClick}
            animate={{ flex: active ? 5 : 1 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="relative rounded-[28px] overflow-hidden cursor-pointer min-h-[88px] md:min-h-[620px] basis-0"
            style={{ minHeight: active ? 460 : undefined }}
            data-testid={`benefit-card-${card.id || index}`}
            data-active={active ? "true" : "false"}
        >
            <img
                src={resolveImg(card.image)}
                alt={card.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                style={{ transform: active ? "scale(1.02)" : "scale(1.08)" }}
            />
            <div
                className="absolute inset-0 transition-opacity duration-700"
                style={{
                    background: active
                        ? "linear-gradient(180deg, rgba(0,0,0,0.10) 30%, rgba(0,0,0,0.72) 100%)"
                        : "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.70) 100%)",
                }}
            />

            {/* Number badge */}
            <div
                className={`absolute top-5 left-5 md:top-6 md:left-6 w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center font-display text-base font-semibold transition-colors duration-700 ${
                    active ? "bg-[#0B3B60] text-white" : "bg-white text-oth-ink"
                }`}
                data-testid={`benefit-card-number-${card.id || index}`}
            >
                {index + 1}
            </div>

            {/* Active content */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
                        className="absolute left-6 right-6 md:left-10 md:right-24 bottom-7 md:bottom-10 text-white"
                    >
                        <h3 className="font-display text-2xl md:text-4xl font-semibold tracking-tight" data-testid={`benefit-card-title-${card.id || index}`}>
                            {card.title}
                        </h3>
                        <p className="mt-3 font-body text-sm md:text-base text-white/85 leading-relaxed max-w-[480px]">
                            {card.body}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* CTA arrow — pinned top-right, only color/background animates (stays put on expand) */}
            <motion.div
                animate={{
                    color: active ? "#0B3B60" : "rgba(255,255,255,0.85)",
                    backgroundColor: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0)",
                }}
                transition={{ duration: 0.6, ease: EASE }}
                className="absolute top-5 right-5 md:top-6 md:right-6 w-9 h-9 rounded-full flex items-center justify-center"
            >
                <ArrowUpRight size={active ? 18 : 16} />
            </motion.div>
        </motion.div>
    );
};

export const Benefits = ({ content }) => {
    const b = content?.benefits || {};
    const cards = (b.cards || []).slice(0, 5);
    const [active, setActive] = useState(0);

    return (
        <section className="relative py-6 md:py-20" data-testid="benefits-section">
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                    <div>
                        <motion.span {...fadeUp(0)} className="text-xs uppercase tracking-[0.24em] text-oth-ink3 font-body">
                            {b.eyebrow}
                        </motion.span>
                        <motion.h2
                            {...fadeUp(0.1)}
                            className="mt-4 font-display font-semibold text-[40px] sm:text-[52px] md:text-[64px] leading-[1.02] tracking-[-0.035em] max-w-[760px]"
                        >
                            {b.headline}
                        </motion.h2>
                    </div>
                    <motion.div
                        {...fadeUp(0.2)}
                        className="rounded-full border border-oth-line/70 bg-white/50 px-4 py-2 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body"
                    >
                        Five quiet wins
                    </motion.div>
                </div>

                <motion.div
                    {...fadeUp(0.1)}
                    className="flex flex-col md:flex-row gap-2.5 p-3 rounded-[40px] surface-soft"
                    data-testid="benefits-accordion"
                    onMouseLeave={() => {/* keep last active */}}
                >
                    {cards.map((card, i) => (
                        <Card
                            key={card.id || i}
                            card={card}
                            index={i}
                            active={active === i}
                            onActivate={() => setActive(i)}
                            onClick={() => setActive(i)}
                        />
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
